import { useState, useEffect } from "react";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import { Link, useLocation } from "react-router-dom";
import { MdKeyboardArrowDown } from "react-icons/md";

const TYPE_LINKS = [
  { label: "Bungalows", to: "/bungalows", base: "/bungalow", categories: [2, 3, 4, 5, "6+"] },
  { label: "Duplex", to: "/duplex", base: "/duplex", categories: [2, 3, 4, 5, "6+"] },
  { label: "Flats", to: "/flat", base: "/flat", categories: [2, 3, 4, 5, "6+"] },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !open;

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setOpen(false);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const closeAll = () => { setOpen(false); setOpenDropdown(null); };
  const toggleMenu = () => { setOpen((p) => !p); setOpenDropdown(null); };
  const toggleDropdown = (label) =>
    setOpenDropdown((prev) => (prev === label ? null : label));

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const linkCls = (active) =>
    `px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
      active
        ? transparent ? "text-white font-bold" : "text-[#1877F2]"
        : transparent
          ? "text-white/90 hover:text-white hover:bg-white/10"
          : "text-gray-700 hover:text-[#1877F2] hover:bg-blue-50"
    }`;

  return (
    <div className="w-full fixed top-0 left-0 z-50 font-Montserrat">
      <nav
        className={`transition-all duration-300 ${
          transparent
            ? "bg-transparent border-transparent shadow-none"
            : "bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-[72px]">

            {/* Logo */}
            <Link to="/" onClick={closeAll} className="flex-shrink-0">
              <img
                src={transparent ? "/images/logo-transparent.png" : "/images/logoWhite-transparent.png"}
                alt="Perfect Shelters"
                className="h-14 md:h-16 w-auto object-contain rounded"
              />
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              <Link to="/" onClick={closeAll} className={linkCls(location.pathname === "/")}>
                Home
              </Link>

              {TYPE_LINKS.map(({ label, to, base, categories }) => (
                <div
                  key={label}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link
                    to={to}
                    className={`flex items-center gap-1 ${linkCls(isActive(base))}`}
                  >
                    {label}
                    <MdKeyboardArrowDown
                      className={`text-base transition-transform duration-200 ${
                        openDropdown === label ? "rotate-180" : ""
                      }`}
                    />
                  </Link>

                  {openDropdown === label && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-52 z-50 pt-1">
                      <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="py-2">
                          <Link
                            to={to}
                            onClick={closeAll}
                            className="block px-4 py-2.5 text-sm text-gray-600 hover:bg-blue-50 hover:text-[#1877F2] font-medium transition-colors"
                          >
                            All {label}
                          </Link>
                          <div className="h-px bg-gray-100 mx-4 my-1" />
                          {categories.map((n) => (
                            <Link
                              key={n}
                              to={`${base}/${n === "6+" ? "6" : n}-bedroom`}
                              onClick={closeAll}
                              className="block px-4 py-2.5 text-sm text-gray-600 hover:bg-blue-50 hover:text-[#1877F2] transition-colors"
                            >
                              {n} Bedroom {label === "Flats" ? "Flat" : label.slice(0, -1)}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <Link to="/about" onClick={closeAll} className={linkCls(location.pathname === "/about")}>
                About
              </Link>

              <div className={`w-px h-5 mx-2 transition-colors duration-300 ${transparent ? "bg-white/30" : "bg-gray-200"}`} />

              <Link
                to="/featured-house-plans"
                onClick={closeAll}
                className={`ml-1 text-sm font-bold px-6 py-2.5 rounded-full transition-all duration-200 shadow-sm ${
                  transparent
                    ? "bg-white text-[#1877F2] hover:bg-white/90"
                    : "bg-[#1877F2] hover:bg-[#0e5fc7] text-white"
                }`}
              >
                Buy Plans
              </Link>
            </div>

            {/* Mobile controls */}
            <div className="flex md:hidden items-center gap-3">
              <Link
                to="/featured-house-plans"
                onClick={closeAll}
                className={`text-xs font-bold px-4 py-2 rounded-full transition-colors duration-200 ${
                  transparent ? "bg-white text-[#1877F2]" : "bg-[#1877F2] text-white"
                }`}
              >
                Buy Plans
              </Link>
              <button
                onClick={toggleMenu}
                aria-label={open ? "Close menu" : "Open menu"}
                className={`transition-colors p-1 ${
                  transparent ? "text-white hover:text-white/80" : "text-gray-700 hover:text-[#1877F2]"
                }`}
              >
                {open ? <AiOutlineClose className="text-xl" /> : <AiOutlineMenu className="text-xl" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden border-t bg-white overflow-hidden transition-all duration-300 ease-in-out ${
            open ? "max-h-[600px] opacity-100 border-gray-100" : "max-h-0 opacity-0 border-transparent"
          }`}
        >
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={closeAll}
              className={`block px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                location.pathname === "/" ? "bg-blue-50 text-[#1877F2]" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              Home
            </Link>

            {TYPE_LINKS.map(({ label, to, base, categories }) => (
              <div key={label}>
                <div className="flex items-center justify-between">
                  <Link
                    to={to}
                    onClick={closeAll}
                    className={`flex-1 block px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                      isActive(base) ? "bg-blue-50 text-[#1877F2]" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </Link>
                  <button
                    onClick={() => toggleDropdown(label)}
                    className="p-3 text-gray-500 hover:text-[#1877F2] transition-colors"
                  >
                    <MdKeyboardArrowDown
                      className={`transition-transform duration-200 ${openDropdown === label ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>

                {openDropdown === label && (
                  <div className="ml-4 mt-1 border-l-2 border-[#1877F2]/30 pl-4 space-y-1">
                    {categories.map((n) => (
                      <Link
                        key={n}
                        to={`${base}/${n === "6+" ? "6" : n}-bedroom`}
                        onClick={closeAll}
                        className="block px-3 py-2.5 text-sm text-gray-600 hover:text-[#1877F2] rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        {n} Bedroom {label === "Flats" ? "Flat" : label.slice(0, -1)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <Link
              to="/about"
              onClick={closeAll}
              className={`block px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                location.pathname === "/about" ? "bg-blue-50 text-[#1877F2]" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              About
            </Link>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
